import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

const CartContext = createContext(null);

const STORAGE = "icp_cart_v1";

/* =========================================================
   CREATE A STABLE VALUE FOR ITEM OPTIONS
   ========================================================= */

function normalizeOptions(options = {}) {
  return {
    size: options.size || "",
    cone: options.cone || "",

    flavours: Array.isArray(options.flavours)
      ? [...options.flavours].sort()
      : [],

    addons: Array.isArray(options.addons)
      ? [...options.addons].sort()
      : [],
  };
}

/* =========================================================
   CHECK WHETHER TWO CART ITEMS ARE THE SAME
   ========================================================= */

function isSameItem(a, b) {
  const optionsA = normalizeOptions(a.options);
  const optionsB = normalizeOptions(b.options);

  return (
    String(a.id ?? "") === String(b.id ?? "") &&
    String(a.name ?? "") === String(b.name ?? "") &&
    Number(a.price ?? 0) === Number(b.price ?? 0) &&
    JSON.stringify(optionsA) ===
      JSON.stringify(optionsB)
  );
}

/* =========================================================
   CART PROVIDER
   ========================================================= */

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE);

      if (!saved) {
        return [];
      }

      const parsed = JSON.parse(saved);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.map((item) => ({
        ...item,
        cartId:
          item.cartId ||
          crypto.randomUUID(),
        quantity: Math.max(
          1,
          Number(item.quantity || 1)
        ),
        options: normalizeOptions(
          item.options
        ),
      }));
    } catch (error) {
      console.error(
        "Unable to load cart:",
        error
      );

      return [];
    }
  });

  /* =======================================================
     SAVE CART
  ======================================================= */

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE,
        JSON.stringify(items)
      );
    } catch (error) {
      console.error(
        "Unable to save cart:",
        error
      );
    }
  }, [items]);

  /* =======================================================
     ADD ITEM
     
     SAME PRODUCT + SAME OPTIONS
     → INCREASE QUANTITY

     DIFFERENT OPTIONS
     → CREATE SEPARATE ITEM
     ======================================================= */

  const add = (item) => {
    setItems((prev) => {
      const quantityToAdd = Math.max(
        1,
        Number(item.quantity || 1)
      );

      const existingIndex =
        prev.findIndex((existingItem) =>
          isSameItem(existingItem, item)
        );

      /* ---------------------------------------------------
         SAME ITEM FOUND
         --------------------------------------------------- */

      if (existingIndex !== -1) {
        return prev.map(
          (existingItem, index) => {
            if (index !== existingIndex) {
              return existingItem;
            }

            return {
              ...existingItem,

              quantity:
                Number(
                  existingItem.quantity || 0
                ) + quantityToAdd,
            };
          }
        );
      }

      /* ---------------------------------------------------
         NEW ITEM
         --------------------------------------------------- */

      return [
        ...prev,
        {
          ...item,

          cartId: crypto.randomUUID(),

          quantity: quantityToAdd,

          options: normalizeOptions(
            item.options
          ),
        },
      ];
    });
  };

  /* =======================================================
     REMOVE ITEM
     ======================================================= */

  const remove = (cartId) => {
    setItems((prev) =>
      prev.filter(
        (item) =>
          item.cartId !== cartId
      )
    );
  };

  /* =======================================================
     SET QUANTITY
     ======================================================= */

  const setQty = (cartId, quantity) => {
    const newQuantity = Number(quantity);

    setItems((prev) => {
      /* ---------------------------------------------------
         If quantity becomes 0 or less, remove the item.
         --------------------------------------------------- */

      if (newQuantity <= 0) {
        return prev.filter(
          (item) =>
            item.cartId !== cartId
        );
      }

      return prev.map((item) =>
        item.cartId === cartId
          ? {
              ...item,
              quantity: newQuantity,
            }
          : item
      );
    });
  };

  /* =======================================================
     CLEAR CART
     ======================================================= */

  const clear = () => {
    setItems([]);

    try {
      localStorage.removeItem(
        STORAGE
      );
    } catch (error) {
      console.error(
        "Unable to clear cart storage:",
        error
      );
    }
  };

  /* =======================================================
     SUBTOTAL
     ======================================================= */

  const subtotal = items.reduce(
    (sum, item) =>
      sum +
      Number(item.price || 0) *
        Number(item.quantity || 0),
    0
  );

  /* =======================================================
     TOTAL ITEM COUNT
     ======================================================= */

  const count = items.reduce(
    (sum, item) =>
      sum + Number(item.quantity || 0),
    0
  );

  /* =======================================================
     PROVIDER
     ======================================================= */

  return (
    <CartContext.Provider
      value={{
        items,
        add,
        remove,
        setQty,
        clear,
        subtotal,
        count,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

/* =========================================================
   USE CART
========================================================= */

export const useCart = () =>
  useContext(CartContext);